/**
 * Resolver that detects state transitions when rooftop access changes.
 *
 * When adding/removing a rooftop, significant side effects can occur:
 * - Role promotion or demotion
 * - Driver access revocation (last lite rooftop removed)
 * - Feature assignment needed (driver gains portal access)
 * - Loss of all rooftop access
 *
 * This pure function determines whether a change is simple (just toggle)
 * or requires admin confirmation via a modal.
 */

import { getUserTierInfo, isLiteTier, type Tier } from '@utils/tiers';
import {
  isDriverOnly,
  isDealerRole,
  getRoleLabel,
  ROLE_DEALER,
  ROLE_DEALER_ADMIN,
} from './roleUtils';
import { hasDriverRole } from './roleUtils';

// ── Types ──

export type ChangeType = 'role-change' | 'driver-access' | 'feature-gap' | 'warning';

export interface RooftopChangeItem {
  type: ChangeType;
  title: string;
  description: string;
  fromRole?: string;
  toRole?: string;
  /** True if admin needs to select features in the modal */
  requiresFeatureSelection?: boolean;
  /** True if driver access will be revoked */
  revokesDriverAccess?: boolean;
}

export interface RooftopChangeResult {
  /** No significant transition — just toggle access */
  isSimple: boolean;
  /** Changes that need confirmation (empty if isSimple) */
  changes: RooftopChangeItem[];
}

export interface RooftopChangeInput {
  /** 'add' or 'remove' */
  action: 'add' | 'remove';
  /** The rooftop being added/removed */
  rooftopTier: Tier;
  /** Current user state */
  user: {
    default_role: string;
    users_usertocustomers: Array<{ customer_id: number; customer?: { tier?: string | null } | null }>;
    users_usertoroles?: Array<{ roletype?: { name?: string } | null }> | null;
    driver?: { id: number } | null;
  };
  /** The customer_id being added/removed */
  customerId: number;
}

// ── Resolver ──

export function resolveRooftopChange(input: RooftopChangeInput): RooftopChangeResult {
  const { action, rooftopTier, user, customerId } = input;
  const changes: RooftopChangeItem[] = [];

  const currentRole = user.default_role;
  const currentTiers = getUserTierInfo(user.users_usertocustomers);
  const currentCount = user.users_usertocustomers.length;
  const userIsDriver = isDriverOnly(currentRole);
  const userIsDealer = isDealerRole(currentRole);
  const userHasDriverRole = hasDriverRole(user.users_usertoroles as any);
  const hasDriverRecord = !!user.driver;

  // Compute what tiers will look like AFTER the change
  const afterCustomers = action === 'add'
    ? [...user.users_usertocustomers, { customer_id: customerId, customer: { tier: rooftopTier } }]
    : user.users_usertocustomers.filter(utc => utc.customer_id !== customerId);
  // Cross-rooftop `customers.tier` read (named exception): the product-access snapshot covers only the working rooftop, and this decision spans every rooftop the user is linked to.
  const afterTiers = getUserTierInfo(afterCustomers);
  const afterCount = afterCustomers.length;

  if (action === 'add') {
    // ── Scenario A: Driver gets standard rooftop → needs promotion ──
    if (userIsDriver && !isLiteTier(rooftopTier)) {
      const targetRole = afterCount >= 2 ? ROLE_DEALER_ADMIN : ROLE_DEALER;
      changes.push({
        type: 'role-change',
        title: 'Role promotion required',
        description: `Adding a Standard rooftop to a Driver requires portal access. This user will be promoted from ${getRoleLabel(currentRole)} to ${getRoleLabel(targetRole)}.`,
        fromRole: currentRole,
        toRole: targetRole,
      });
      changes.push({
        type: 'feature-gap',
        title: 'Feature assignment needed',
        description: 'This user needs portal features assigned to access the dealer portal.',
        requiresFeatureSelection: true,
      });
    }

    // ── Scenario A variant: Driver gets any rooftop that triggers promotion ──
    // (driver with existing standard rooftops getting another — already a dealer, handled below)

    // ── Scenario B: Standard-only dealer gets first lite rooftop ──
    if (userIsDealer && currentTiers.isStandardOnly && isLiteTier(rooftopTier)) {
      changes.push({
        type: 'driver-access',
        title: 'Driver eligibility unlocked',
        description: 'This user will now have access to a Lite rooftop, making them eligible for mobile app / driver access.',
      });
    }

    // ── Scenario C: Dealer with 1 rooftop gets a 2nd → dealer-admin ──
    if (currentRole === ROLE_DEALER && currentCount === 1) {
      changes.push({
        type: 'role-change',
        title: 'Role promotion',
        description: `This user will be promoted from ${getRoleLabel(ROLE_DEALER)} to ${getRoleLabel(ROLE_DEALER_ADMIN)} because they will have access to multiple rooftops.`,
        fromRole: ROLE_DEALER,
        toRole: ROLE_DEALER_ADMIN,
      });
    }

    // Scenario D: Driver gets another lite rooftop → no role change, simple toggle
    // (falls through to isSimple)

  } else {
    // action === 'remove'

    // ── Scenario E: Dealer-admin losing 2nd-to-last rooftop → dealer ──
    if (currentRole === ROLE_DEALER_ADMIN && afterCount === 1) {
      changes.push({
        type: 'role-change',
        title: 'Role demotion',
        description: `This user will be demoted from ${getRoleLabel(ROLE_DEALER_ADMIN)} to ${getRoleLabel(ROLE_DEALER)} because they will have access to only one rooftop.`,
        fromRole: ROLE_DEALER_ADMIN,
        toRole: ROLE_DEALER,
      });
    }

    // ── Scenario F: Combo user losing last lite rooftop → driver access revoked ──
    if ((userHasDriverRole || hasDriverRecord) && currentTiers.hasLite && !afterTiers.hasLite) {
      changes.push({
        type: 'driver-access',
        title: 'Driver access will be revoked',
        description: 'This user will have no Lite rooftops remaining. Mobile app / driver access requires at least one Lite rooftop and will be revoked.',
        revokesDriverAccess: true,
      });
    }

    // ── Scenario G: User losing last rooftop entirely ──
    if (afterCount === 0) {
      changes.push({
        type: 'warning',
        title: 'No rooftop access',
        description: 'This user will have no rooftop access after this change. They will not be able to use the portal or mobile app.',
      });
    }

    // Scenario H: Combo user losing last standard (still has lite) → simple
    // (falls through to isSimple)
  }

  return {
    isSimple: changes.length === 0,
    changes,
  };
}
